import DOMPurify from 'dompurify';
import { PIMCORE_CONTENT_BASE_URL } from './Pimcore';

export function sanitizeHtml(html: string): string {
    let sanitized = DOMPurify.sanitize(html);

    // fix relateive URLs in the HTML content
    sanitized = sanitized
        // images are stored as assets in Pimcore but proxied by our backend
        .replaceAll(
            'srcset="',
            `srcset="${PIMCORE_CONTENT_BASE_URL}/Resource%20Library/_assets/images/`
        )
        .replaceAll(
            'src="/Resource%20Library',
            `src="${PIMCORE_CONTENT_BASE_URL}/Resource%20Library`
        )

        // links can point to any asset in Pimcore, not only inmages or HTML, so we need to
        // replace them with the full Pimcore URL until they get proxied by our backend too
        .replaceAll(
            'href="/Resource%20Library',
            `href="${PIMCORE_CONTENT_BASE_URL}/Resource%20Library`
        );

    return sanitized;
}

/**
 * Add base to root-relative src and srcset URLs.
 * - src="/..."     -> src="<base>/..."
 * - srcset="/a 1x, /b 2x" -> "<base>/a 1x, <base>/b 2x"
 */
export function fixPimcoreRootRelativeSrcs(
    html: string,
    removePimcoreIdIfHasSrc: boolean = false
): string {
    const base = PIMCORE_CONTENT_BASE_URL.replace(/\/+$/, '');

    // Prefix root-relative src (avoid protocol-relative //)
    let fixed = html.replace(
        /(^|[\s<])src=(["'])(\/(?!\/)[^"']*)\2/gi,
        (_m, lead: string, quote: string, path: string) =>
            `${lead}src=${quote}${base}${path}${quote}`
    );

    // Prefix each root-relative URL inside every srcset (match each attribute independently)
    fixed = fixed.replace(
        /(^|[\s<])srcset=(["'])([^"']*)\2/gi,
        (_m, lead: string, quote: string, value: string) => {
            const updated = value.replace(
                /(^|,\s*)(\/(?!\/)[^,\s]+)/g,
                (_m2, l: string, p: string) => `${l}${base}${p}`
            );
            return `${lead}srcset=${quote}${updated}${quote}`;
        }
    );

    return removePimcoreIdIfHasSrc ? stripPimcoreIdOnTagsWithSources(fixed) : fixed;
}

/**
 * Remove `pimcore_id` from tags that already have a usable `src` or `srcset`.
 */
export function stripPimcoreIdOnTagsWithSources(html: string): string {
    return html.replace(
        /<([a-zA-Z][^\s/>]*)([^>]*?)(\/?)>/g,
        (full, tag: string, attrs: string, selfClose: string) => {
            const hasSource = /(?:^|[\s/])(?:src|srcset)\s*=/i.test(attrs);
            const hasPimcoreId = /(?:^|[\s/])pimcore_id\s*=/i.test(attrs);
            if (!hasSource || !hasPimcoreId) return full;

            const newAttrs = attrs.replace(
                /(\s+)pimcore_id\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi,
                ''
            );
            return `<${tag}${newAttrs}${selfClose}>`;
        }
    );
}
